import React from 'react';
import { Link } from 'react-router-dom';

import {
    Box,
    Table,
    Icon,
    Link as HTTPLink,
    Modal,
    Popover,
    StatusIndicator,
    Button,
    Badge,
    Alert,
    TableProps,
    SpaceBetween,
    Header,
    CopyToClipboard,
} from '@amzn/awsui-components-react-v3';

import {
    formatNumber,
    formatGrimsbyDate,
    formatDeliverySessionTime,
    showEditButton,
    currencyFormatter,
    AudienceType,
    ActivityStatus,
    ActivityLMSType,
    ActivityModality,
    ActivityProgram,
    ActivityViltType,
    getEditButtonProps,
    ActivityType,
    WaitlistEnabled,
    getEffectiveLmsId,
    getLmsRegistrationLink,
} from '../../Common/Common';
import Section, {
    SectionProps,
    ColumnContentData,
    KeyValueData,
} from '../../../../../common/components/Section/Section';
import {
    EMPTY_STRING,
    SectionContentType,
} from '../../../../../common/constants/grimsby';
import { Actions } from '../../../../../common/constants/auth';
import Can from '../../../../../common/components/Can';
import { getUserRoles } from '../../../../../common/utils/auth';
import TableHeader from '../../../../../common/components/TableHeader/TableHeader';
import {
    ActivityData,
    CostItem,
    Customer,
    PaymentStatus,
} from '../../../../interfaces/activity';
import CreateKikuClassDialog from './CreateKikuClassDialog';
import { formatStringArray } from '../../../../../common/utils/formatStringArray';
import { formatString } from '../../../../../common/utils/formatString';
import { formatBoolean } from '../../../../../common/utils/formatBoolean';
import { getFullUrl } from '../../../../../common/utils/getFullUrl';
import { truncateUrl } from '../../../../../common/utils/truncateUrl';

import {
    DeliverySessionItem,
    CostTableItem,
    InstructorItem,
    RegistrationLinkDisplayInfo,
} from './DetailsInterfaces';
import './DetailsTab.css';
import { CreateLMSClassResponse } from '../../../../interfaces/lmsServiceResponse';
import getKikuURL from '../../../../../common/utils/getKikuURL';

import { ClassroomStatusPanel } from './ClassroomStatusPanel';
import SupergraphEnrichmentSection from './SupergraphEnrichmentSection';
import { activityNeedsClassroom } from '../../Common/classroomEligibilityRules';
import DisplayEditButton from '../../Common/DisplayEditButton';

import renderClassroomStatusMessage from './ClassroomStatusRenderer';
import { checkFeature } from '../../../../../common/utils/featureFlag';
import { isSupergraphEnrichmentEnabled } from '../../../../../common/utils/supergraphFeature';
import { useSelector } from 'react-redux';
import { selectFeatures } from '../../../../../common/store/slices/featureSlice';
import { InfoLink } from '../../../../../common/components/InfoLink/InfoLink';
import { useHelpPanel } from '../../../../../common/context/HelpPanelContext';
import {
    CLASSROOM_PANEL_CONTENT,
    INSTRUCTOR_ASSIGNMENT_HELP_PANEL_CONTENT,
} from '../../../../../common/components/HelpPanel/HelpPanelContent';
import { ACI_PROGRAM_NAME } from '../../../../constants/aci';
import OrganizationGoalsDisplay from './OrganizationGoalsDisplay';
import { hasOrganizationId } from '../../FormSections/EditBaseDetailsFormSection/Sections/OrganizationGoalsSelect';

export const deliverySessionColumnDefinition = (
    program: string,
    featureFlags?: ReturnType<typeof selectFeatures>,
): Array<TableProps.ColumnDefinition<DeliverySessionItem>> => {
    const columnDefinitions = [
        {
            id: 'Date',
            header: 'Date',
            cell: (item: DeliverySessionItem) => <>{formatString(item.date)}</>,
        },
        {
            id: 'Time',
            header: 'Time',
            cell: (item: DeliverySessionItem) => <>{formatString(item.time)}</>,
        },
    ];

    if (
        checkFeature(
            '',
            { featureName: 'per-session-instructor-assignment' },
            featureFlags?.features,
        )
    ) {
        columnDefinitions.push({
            id: 'Instructors',
            header: 'Instructor',
            cell: (item: DeliverySessionItem) => {
                if (!item.instructors || item.instructors.length === 0) {
                    return <></>;
                }
                const sortedNames = item.instructors
                    .map((inst) => inst.name)
                    .sort((a, b) => a.localeCompare(b));

                return <>{sortedNames.join(', ')}</>;
            },
        });
    }

    if (program === ACI_PROGRAM_NAME) {
        columnDefinitions.push({
            id: 'StreamyardLink',
            header: 'Streamyard Link',
            cell: (item: DeliverySessionItem) => {
                if (item.streamyard_url) {
                    return (
                        <HTTPLink
                            href={getFullUrl(item.streamyard_url)}
                            external
                        >
                            {truncateUrl(item.streamyard_url)}
                        </HTTPLink>
                    );
                } else {
                    return <> - </>;
                }
            },
        });
    }

    return columnDefinitions;
};

export const instructorsColumnDefinition: Array<
    TableProps.ColumnDefinition<InstructorItem>
> = [
    {
        id: 'Name',
        header: 'Name',
        cell: (item: InstructorItem) => (
            <Link to={'/instructors/' + item.pk}>
                {formatString(item.name)}
                {item.type === 'External' && (
                    <Badge className="fl-badge" color="blue">
                        FL
                    </Badge>
                )}
            </Link>
        ),
    },
    {
        id: 'Role',
        header: 'Role',
        cell: (item: InstructorItem) => formatString(item.role),
    },
    {
        id: 'Email',
        header: 'Email',
        cell: (item: InstructorItem) => formatString(item.email),
    },
    {
        id: 'Type',
        header: 'Type',
        cell: (item: InstructorItem) => formatString(item.type),
    },
    {
        id: 'Location',
        header: 'Location',
        cell: (item: InstructorItem) => formatString(item.location),
    },
    {
        id: 'DoNotShuffle',
        header: 'Do not shuffle',
        cell: (item: InstructorItem) => item.doNotShuffle,
    },
];

export const costItemsColumnDefinition: Array<
    TableProps.ColumnDefinition<CostTableItem>
> = [
    {
        id: 'CostType',
        header: 'Cost Type',
        cell: (item: CostTableItem) => formatString(item.costType),
    },
    {
        id: 'CostAmount',
        header: 'Cost Amount',
        cell: (item: CostTableItem) => formatNumber(item.costAmount),
    },
    {
        id: 'PaymentPO',
        header: 'Payment PO# or Pcard',
        cell: (item: CostTableItem) => formatString(item.paymentPoNumber),
    },
    {
        id: 'PaymentStatus',
        header: 'Payment status',
        cell: (item: CostTableItem) => item.paymentStatus,
    },
];

export const formatPaymentStatus = (
    costPaymentStatus: PaymentStatus | null,
): JSX.Element => {
    if (costPaymentStatus === PaymentStatus.PendingPayment) {
        return (
            <span className="awsui-util-status-pending nowrap">
                <Icon name="status-pending" />
                &nbsp;{costPaymentStatus}
            </span>
        );
    } else if (costPaymentStatus === PaymentStatus.VendorPaid) {
        return (
            <span className="awsui-util-status-stopped nowrap">
                <Icon name="status-stopped" />
                &nbsp;{costPaymentStatus}
            </span>
        );
    } else {
        return (
            <span className="awsui-util-status-positive nowrap">
                <Icon name="status-positive" />
                &nbsp;{PaymentStatus.PiggyBank}
            </span>
        );
    }
};

const formatCustomers = (customers: Array<Customer> | null) =>
    customers?.reduce((acc, customer, index) => {
        if (customer.customer_name) {
            acc = `${acc}${index === 0 ? '' : ', '}${customer.customer_name}`;
        }
        return acc;
    }, '') || EMPTY_STRING;

const formatActivityColumnData = (
    keyValueData: KeyValueData,
    activity: ActivityData,
    selectedModality: 'vILT' | 'ILT' | 'HYBRID_MODALITY',
) => {
    if (selectedModality === 'HYBRID_MODALITY') {
        return [keyValueData];
    }
    return activity.activity_modality === selectedModality
        ? [keyValueData]
        : [];
};

const renderViltMeetingCreationStatus = (status: string | null) => {
    if (!status) {
        return EMPTY_STRING;
    }

    // Success case: "COMPLETED"
    if (status === 'COMPLETED') {
        return <StatusIndicator type="success">{status}</StatusIndicator>;
    }

    // Pending case: "COMPLETED"
    if (status === 'PROCESSING') {
        return <StatusIndicator type="in-progress">PROCESSING</StatusIndicator>;
    }

    // Error case: strings starting with "ERROR"
    if (status.startsWith('ERROR')) {
        return <StatusIndicator type="error">ERROR</StatusIndicator>;
    }

    // All other cases: return as plain text
    return status;
};

const DetailsTab = ({
    activity,
    onActivityDetailsUpdated,
    isACIFeatureEnabled,
}: {
    activity: ActivityData;
    onActivityDetailsUpdated: () => void;
    isACIFeatureEnabled: boolean;
}) => {
    const featureFlags = useSelector(selectFeatures);
    const canShowWaitlistEnabledComponent = !!checkFeature(
        '',
        { featureName: 'waitlist-option-select' },
        featureFlags?.features,
    );
    const isLearningGoalsEnabled = !!checkFeature(
        '',
        { featureName: 'learning-goals' },
        featureFlags?.features,
    );
    const isSupergraphEnabled = isSupergraphEnrichmentEnabled(featureFlags);
    const userRoles = getUserRoles();
    const costItems = activity.cost_items;
    const displayEditButton = showEditButton(activity);
    const [showRegistrationLinks, setShowRegistrationLinks] =
        React.useState(false);
    const [kikuErrors, setKikuErrors] = React.useState<Array<string>>([]);
    const [kikuWarnings, setKikuWarnings] = React.useState<Array<string>>([]);
    const {
        actions: { makeHelpPanelHandler },
    } = useHelpPanel();
    const cancellationReason =
        activity.activity_status === ActivityStatus.Canceled &&
        activity.cancellation_reason
            ? ': ' + activity.cancellation_reason
            : '';
    const isILT = activity.activity_modality === ActivityModality.ILT;
    const isPartnerProgram = activity.program === ActivityProgram.Partner;
    const isTopicNameRequired = activity.topic_name != null;
    const activityDetailsProps: SectionProps = {
        testId: 'ActivityDetailsSection',
        header: {
            label: 'Activity details',
            buttons: (
                <DisplayEditButton
                    {...getEditButtonProps({
                        activity,
                        testId: 'ActivityDetailsEditButton',
                        path: 'edit-activity-details',
                        isACIFeatureEnabled,
                    })}
                />
            ),
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 4,
            columns: [
                [
                    {
                        key: 'Activity status',
                        value:
                            formatString(activity.activity_status) +
                            cancellationReason,
                    },
                    {
                        key: 'Modality',
                        value: formatString(activity.activity_modality),
                    },
                ],
                [
                    {
                        key: 'Activity type',
                        value: formatString(activity.activity_type),
                    },
                    {
                        key: 'Provider',
                        value: formatString(activity.provider),
                    },
                ],
                [
                    {
                        key: 'Program',
                        value: formatString(activity.program),
                    },
                    isPartnerProgram
                        ? {
                              key: 'Partner initiative',
                              value: formatString(activity.partner_initiative),
                          }
                        : null,
                    {
                        key: 'Customers',
                        value: formatCustomers(activity.customers),
                    },
                ],
                [
                    {
                        key: 'Audience',
                        value: formatString(activity.activity_audience),
                    },
                    !!checkFeature(
                        '',
                        { featureName: 'activity-name-visibility' },
                        featureFlags?.features,
                    )
                        ? {
                              key: 'Activity name status',
                              value:
                                  activity.activity_name_visibility === 'PUBLIC'
                                      ? 'Approved for customers'
                                      : 'Not Approved',
                          }
                        : null,
                    hasOrganizationId(activity.customers) &&
                    isLearningGoalsEnabled
                        ? {
                              key: 'Organization learning goals',
                              value: (
                                  <OrganizationGoalsDisplay
                                      customers={activity.customers}
                                      selectedGoalIds={
                                          activity.learning_goal_ids
                                      }
                                  />
                              ),
                          }
                        : null,
                ],
            ],
        } as ColumnContentData,
    };

    const publicOperationsDetailsProps: SectionProps = {
        testId: 'OperationDetailsSection',
        className: 'awsui-util-mb-n',
        header: {
            label: 'Operations details',
            buttons: (
                <DisplayEditButton
                    {...getEditButtonProps({
                        activity,
                        testId: 'OperationsDetailsSectionEditButton',
                        path: 'edit-operations-and-registration-details',
                        isACIFeatureEnabled,
                    })}
                />
            ),
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 4,
            columns: [
                [
                    {
                        key: 'Operations owner',
                        value: formatString(activity.operations_owner),
                    },
                    {
                        key: 'Catering requirement',
                        value: formatBoolean(activity.catering_required, 'Yes'),
                    },
                ],
                [
                    activity.additional_owners?.length
                        ? {
                              key: 'Secondary owner',
                              value: formatStringArray(
                                  activity.additional_owners?.map(
                                      (owner) => owner.additional_owner_name,
                                  ),
                              ),
                          }
                        : null,
                    {
                        key: 'Scheduler',
                        value: formatString(activity.scheduler),
                    },
                    {
                        key: 'CSM/BDM/CEM',
                        value: formatString(activity.customer_support_manager),
                    },
                    {
                        key: 'Requester',
                        value: formatString(activity.requestor),
                    },
                    {
                        key: 'Class request SIM',
                        value: activity?.class_request_sim ? (
                            activity.class_request_sim.substring(0, 4) ===
                            'http' ? (
                                <HTTPLink
                                    href={activity.class_request_sim}
                                    target="_blank"
                                    data-testid="class-sim-link"
                                >
                                    {formatString(activity.class_request_sim)}
                                </HTTPLink>
                            ) : (
                                formatString(activity.class_request_sim)
                            )
                        ) : (
                            EMPTY_STRING
                        ),
                    },
                ],
                [
                    {
                        key: 'Cost currency',
                        value: formatString(activity.cost_currency),
                    },
                    {
                        key: 'Billed currency',
                        value: formatString(activity.billed_currency),
                    },
                ],
                [
                    {
                        key: 'Attended',
                        value: formatNumber(activity.attended),
                    },
                    {
                        key: 'Total billed revenue',
                        value:
                            activity.total_billed_revenue &&
                            activity.billed_currency
                                ? currencyFormatter(
                                      activity.total_billed_revenue,
                                      activity.billed_currency
                                          ? activity.billed_currency
                                          : 'USD',
                                  )
                                : formatNumber(activity.total_billed_revenue),
                    },
                ],
            ],
        } as ColumnContentData,
    };

    const otherOperationsDetailsProps: SectionProps = {
        testId: 'OperationDetailsSection',
        className: 'awsui-util-mb-n',
        header: {
            label: 'Operations details',
            buttons: (
                <DisplayEditButton
                    {...getEditButtonProps({
                        activity,
                        testId: 'OperationsDetailsSectionEditButton',
                        path: 'edit-operations-and-registration-details',
                        isACIFeatureEnabled,
                    })}
                />
            ),
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 4,
            columns: [
                [
                    {
                        key: 'Operations owner',
                        value: formatString(activity.operations_owner),
                    },
                    isILT
                        ? {
                              key: 'Catering requirement',
                              value: formatBoolean(
                                  activity.catering_required,
                                  'Yes',
                              ),
                          }
                        : {
                              key: 'Seller Country',
                              value: (
                                  <>
                                      {formatString(
                                          activity.sor_country ||
                                              activity.delivery_country,
                                      )}
                                      {activity.sor_country &&
                                          activity.delivery_country &&
                                          activity.sor_country !==
                                              activity.delivery_country && (
                                              <>
                                                  {' '}
                                                  <Badge color="blue">
                                                      Cross-geo
                                                  </Badge>
                                              </>
                                          )}
                                  </>
                              ),
                          },
                ],
                [
                    activity.additional_owners?.length
                        ? {
                              key: 'Secondary owner',
                              value: formatStringArray(
                                  activity.additional_owners?.map(
                                      (owner) => owner.additional_owner_name,
                                  ),
                              ),
                          }
                        : null,
                    {
                        key: 'Scheduler',
                        value: formatString(activity.scheduler),
                    },
                    {
                        key: 'CSM/BDM/CEM',
                        value: formatString(activity.customer_support_manager),
                    },
                    {
                        key: 'Requester',
                        value: formatString(activity.requestor),
                    },
                    {
                        key: 'Class request SIM',
                        value: activity?.class_request_sim ? (
                            activity.class_request_sim.substring(0, 4) ===
                            'http' ? (
                                <HTTPLink
                                    href={activity.class_request_sim}
                                    target="_blank"
                                    data-testid="class-sim-link"
                                >
                                    {formatString(activity.class_request_sim)}
                                </HTTPLink>
                            ) : (
                                formatString(activity.class_request_sim)
                            )
                        ) : (
                            EMPTY_STRING
                        ),
                    },
                ],
                [
                    {
                        key: 'Cost currency',
                        value: formatString(activity.cost_currency),
                    },
                ],
                [
                    {
                        key: 'Attended',
                        value: formatNumber(activity.attended),
                    },
                ],
            ],
        } as ColumnContentData,
    };

    const formatUrlForDisplay = (url: string) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname;
        } catch {
            return url.length > 30 ? `${url.substring(0, 30)}...` : url;
        }
    };

    const getvILTLink = () => {
        if (!activity?.v_ilt_id) {
            return EMPTY_STRING;
        }

        const isUrl = activity.v_ilt_id.substring(0, 4) === 'http';
        const displayText = isUrl
            ? formatUrlForDisplay(activity.v_ilt_id)
            : activity.v_ilt_id;

        return (
            <SpaceBetween direction="horizontal" size="xs">
                {isUrl ? (
                    <span title={activity.v_ilt_id}>
                        <HTTPLink
                            href={activity.v_ilt_id}
                            target="_blank"
                            data-testid="vilt-link"
                        >
                            {displayText}
                        </HTTPLink>
                    </span>
                ) : (
                    <span title={activity.v_ilt_id}>{displayText}</span>
                )}
                <CopyToClipboard
                    copyButtonText="Copy vILT link"
                    copyErrorText="Failed to copy"
                    copySuccessText="vILT link copied"
                    textToCopy={activity.v_ilt_id}
                    variant="icon"
                />
            </SpaceBetween>
        );
    };

    const copyOneClickLink = (link: string) => {
        navigator.clipboard.writeText(link);
    };

    const registrationLinkInfo = (linkDetails: RegistrationLinkDisplayInfo) => {
        if (activity.lms_type === ActivityLMSType.Kiku) {
            return {
                key: linkDetails.description,
                value: !!activity[linkDetails.attributeName] ? (
                    <Popover
                        size="small"
                        position="top"
                        triggerType="custom"
                        dismissButton={false}
                        content={
                            <StatusIndicator type="success">
                                Link copied
                            </StatusIndicator>
                        }
                    >
                        <button
                            onClick={() =>
                                copyOneClickLink(
                                    activity[
                                        linkDetails.attributeName
                                    ]?.toString() ?? '',
                                )
                            }
                            className="nowrap unstyled-button"
                        >
                            <Icon name={'copy'} />
                            <span className="registration-link">
                                {activity[linkDetails.attributeName]}
                            </span>
                        </button>
                    </Popover>
                ) : (
                    formatString('')
                ),
            };
        }

        return {};
    };

    const CopyLinkButton = (url: string) => {
        return (
            <Popover
                size="small"
                position="top"
                triggerType="custom"
                dismissButton={false}
                content={
                    <StatusIndicator type="success">
                        Link copied
                    </StatusIndicator>
                }
            >
                <Button
                    iconName="copy"
                    variant="normal"
                    onClick={() => copyOneClickLink(url)}
                >
                    Copy
                </Button>
            </Popover>
        );
    };

    const roleSpecificLinks = () => {
        if (activity.lms_type !== ActivityLMSType.Kiku) {
            return [];
        }

        return [
            {
                key: 'Role-specific registration links',
                value: !!activity.lms_id ? (
                    <>
                        <button
                            onClick={() => setShowRegistrationLinks(true)}
                            className="view-links"
                        >
                            View links
                        </button>
                        <Modal
                            size={'medium'}
                            visible={showRegistrationLinks}
                            onDismiss={() => {
                                setShowRegistrationLinks(false);
                            }}
                            header={'Registration links'}
                            footer={
                                <Box float="right">
                                    <Button
                                        variant="primary"
                                        onClick={() =>
                                            setShowRegistrationLinks(false)
                                        }
                                    >
                                        Close
                                    </Button>
                                </Box>
                            }
                        >
                            <div className="activity-link-container">
                                <div className="link-label">
                                    Amazon employees
                                </div>
                                <div className="link-url-container">
                                    <div className="link-box">
                                        https://kiku.aws.training/learningobject/ilt?id=
                                        {activity.lms_id}
                                    </div>
                                    {CopyLinkButton(
                                        `https://kiku.aws.training/learningobject/ilt?id=${activity.lms_id}`,
                                    )}
                                </div>
                                <div className="link-label">Anonymous</div>
                                <div className="link-url-container">
                                    <div className="link-box">
                                        https://www.aws.training/learningobject/ilt?id=
                                        {activity.lms_id}
                                    </div>
                                    {CopyLinkButton(
                                        `https://www.aws.training/learningobject/ilt?id=${activity.lms_id}`,
                                    )}
                                </div>
                                <div className="link-label">APN members</div>
                                <div className="link-url-container">
                                    <div className="link-box">
                                        https://partnercentral.awspartner.com/LmsSsoRedirect?RelayState=%2flearningobject%2filt%3fid%3d
                                        {activity.lms_id}
                                    </div>
                                    {CopyLinkButton(
                                        `https://partnercentral.awspartner.com/LmsSsoRedirect?RelayState=%2flearningobject%2filt%3fid%3d${activity.lms_id}`,
                                    )}
                                </div>
                                <div className="link-label">Customers</div>
                                <div className="link-url-container">
                                    <div className="link-box">
                                        https://www.aws.training/account/logonoptions?returnUrl=%2flearningobject%2filt%3fid%3d
                                        {activity.lms_id}
                                    </div>
                                    {CopyLinkButton(
                                        `https://www.aws.training/account/logonoptions?returnUrl=%2flearningobject%2filt%3fid%3d${activity.lms_id}`,
                                    )}
                                </div>
                            </div>
                        </Modal>
                    </>
                ) : (
                    formatString('')
                ),
            },
        ];
    };

    const handleSaveKikuClassResponse = (response: CreateLMSClassResponse) => {
        setKikuErrors(response?.errors ?? []);
        setKikuWarnings(response?.warnings ?? []);

        if (response?.errors.length === 0) {
            onActivityDetailsUpdated();
        }
    };

    const showCreateKikuButton = !['Cancelled', 'Completed'].includes(
        activity.activity_status,
    );
    const showKikuWarnings = kikuWarnings.length > 0 && kikuErrors.length === 0;
    const kikuWarningList = React.useMemo(() => {
        return (
            <>
                <span>Warnings:</span>
                <ul>
                    {kikuWarnings.map((warning, i) => (
                        <li key={`kiku-warning-${i}`}>{warning}</li>
                    ))}
                </ul>
            </>
        );
    }, [kikuWarnings]);
    const kikuErrorList = React.useMemo(() => {
        return (
            <>
                <span>Errors:</span>
                <ul>
                    {kikuErrors.map((error, i) => (
                        <li key={`kiku-error-${i}`}>{error}</li>
                    ))}
                </ul>
            </>
        );
    }, [kikuErrors]);

    const classroomStatus = activity.classrooms_status;
    const classroomActivityType = activity.activity_type;
    const classroomProgramType = activity.program;
    const classroomLmsType = activity.lms_type;

    const isWaitlistEnabled =
        classroomProgramType === ActivityProgram.SkillsCenters ||
        classroomLmsType === ActivityLMSType.MyClass;

    const isMyClassAdcEnabled = !!checkFeature(
        '',
        { featureName: 'MYCLASS_ADC_ENABLED' },
        featureFlags?.features,
    );

    const shouldShowClassroomsDetailsTab = activityNeedsClassroom(
        { program: classroomProgramType, activity_type: classroomActivityType },
        { isMyClassAdcEnabled },
    );

    const registrationDetailsProps: SectionProps = {
        testId: 'RegistrationDetailsSection',
        className: 'awsui-util-mb-n',
        header: {
            label: 'Registration details',
            buttons: (
                <SpaceBetween size="xs" direction="horizontal">
                    {displayEditButton ? (
                        <>
                            <Can
                                roles={userRoles}
                                perform={Actions.ACTIVITY_MODIFY}
                                yes={() => (
                                    <>
                                        {showCreateKikuButton === true && (
                                            <CreateKikuClassDialog
                                                activity={activity}
                                                onSave={
                                                    handleSaveKikuClassResponse
                                                }
                                            />
                                        )}
                                    </>
                                )}
                                no={() => null}
                            />
                        </>
                    ) : null}
                </SpaceBetween>
            ),
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 4,
            genericContent: (
                <>
                    {kikuErrors.length > 0 && (
                        <Alert type="error">{kikuErrorList}</Alert>
                    )}
                    {showKikuWarnings && (
                        <Alert type="warning">{kikuWarningList}</Alert>
                    )}
                </>
            ),
            columns: [
                [
                    {
                        key: 'Class size',
                        value: formatNumber(activity.class_size),
                    },
                    {
                        key: 'LMS ID',
                        value: (() => {
                            const effectiveLmsId = getEffectiveLmsId(activity);
                            const lmsLink = getLmsRegistrationLink(activity);

                            if (!effectiveLmsId) return EMPTY_STRING;

                            if (lmsLink) {
                                return (
                                    <HTTPLink
                                        href={lmsLink}
                                        target="_blank"
                                        data-testid="lms-link"
                                    >
                                        {formatString(effectiveLmsId)}
                                    </HTTPLink>
                                );
                            }

                            // For Kiku or when no link available
                            if (activity.lms_type === ActivityLMSType.Kiku) {
                                return (
                                    <HTTPLink
                                        href={getKikuURL(effectiveLmsId)}
                                        target="_blank"
                                        data-testid="lms-link"
                                    >
                                        {formatString(effectiveLmsId)}
                                    </HTTPLink>
                                );
                            }

                            return formatString(effectiveLmsId);
                        })(),
                    },
                ],
                [
                    ...formatActivityColumnData(
                        {
                            key: 'vILT type',
                            value: formatString(activity.v_ilt_type),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                    ...formatActivityColumnData(
                        {
                            key: 'vILT link',
                            value: activity.v_ilt_id
                                ? getvILTLink()
                                : EMPTY_STRING,
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                    ...formatActivityColumnData(
                        {
                            key: 'vILT Meeting ID',
                            value: activity.v_ilt_meeting_id ? (
                                <SpaceBetween direction="horizontal" size="xs">
                                    <span>{activity.v_ilt_meeting_id}</span>
                                    <CopyToClipboard
                                        copyButtonText="Copy vILT Meeting ID"
                                        copyErrorText="Failed to copy"
                                        copySuccessText="vILT Meeting ID copied"
                                        textToCopy={activity.v_ilt_meeting_id}
                                        variant="icon"
                                    />
                                </SpaceBetween>
                            ) : (
                                EMPTY_STRING
                            ),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                    ...roleSpecificLinks(),
                    ...formatActivityColumnData(
                        {
                            key: 'vILT Meeting creation status',
                            value: renderViltMeetingCreationStatus(
                                activity.v_ilt_meeting_creation_status,
                            ),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                ],
                [
                    {
                        key: 'Registered',
                        value: formatNumber(activity.registered),
                    },
                    {
                        key: 'Waitlisted',
                        value: formatNumber(activity.waitlisted),
                    },
                    ...(canShowWaitlistEnabledComponent && isWaitlistEnabled
                        ? [
                              ...formatActivityColumnData(
                                  {
                                      key: 'Waitlist Option',
                                      value:
                                          activity.waitlist_enabled ||
                                          WaitlistEnabled.Disabled,
                                  },
                                  activity,
                                  'HYBRID_MODALITY',
                              ),
                          ]
                        : []),
                    registrationLinkInfo({
                        description: 'LMS one-click registration link',
                        attributeName: 'lms_one_click_registration_link',
                    }),
                ],
                [
                    {
                        key: 'LMS type',
                        value: formatString(activity.lms_type),
                    },
                    activity.customer_lms_platform
                        ? {
                              key: 'CustomerLMS Platform',
                              value: formatString(
                                  activity.customer_lms_platform,
                              ),
                          }
                        : null,
                    registrationLinkInfo({
                        description: 'APN Merged Registration Link',
                        attributeName: 'apn_merged_registration_link',
                    }),
                    registrationLinkInfo({
                        description: 'Commercial Merged Registration Link',
                        attributeName: 'commercial_merged_registration_link',
                    }),
                ],
            ],
        } as ColumnContentData,
    };

    const classroomUrlsDetailsProps: SectionProps = {
        testId: 'ClassroomUrlsDetailsSection',
        className: 'awsui-util-mb-n',
        header: {
            label: 'Classroom URLs',
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 1,
            columns: [
                [
                    ...formatActivityColumnData(
                        {
                            key: 'ARN',
                            value: activity.classrooms_arn || EMPTY_STRING,
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                    ...formatActivityColumnData(
                        {
                            key: 'Student URL',
                            value: activity.classrooms_student_url ? (
                                <HTTPLink
                                    external
                                    href={activity.classrooms_student_url}
                                    variant="secondary"
                                >
                                    {activity.classrooms_student_url}
                                </HTTPLink>
                            ) : (
                                EMPTY_STRING
                            ),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                    ...formatActivityColumnData(
                        {
                            key: 'Instructor URL',
                            value: activity.classrooms_instructor_url ? (
                                <HTTPLink
                                    external
                                    href={activity.classrooms_instructor_url}
                                    variant="secondary"
                                >
                                    {activity.classrooms_instructor_url}
                                </HTTPLink>
                            ) : (
                                EMPTY_STRING
                            ),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                ],
            ],
        } as ColumnContentData,
    };

    // Show URLs section only when classroom has been created and URLs are available
    const hasClassroomUrls =
        activity.classrooms_arn ||
        activity.classrooms_student_url ||
        activity.classrooms_instructor_url;

    const getCostItems = () => {
        return (
            costItems?.map(
                (costItem: CostItem) =>
                    ({
                        costType: formatString(costItem.cost_type),
                        costAmount: costItem.cost_amount,
                        paymentPoNumber: costItem.payment_po_number_or_pcard,
                        paymentStatus: formatPaymentStatus(
                            costItem.payment_status as PaymentStatus,
                        ),
                    }) as CostTableItem,
            ) ?? []
        );
    };

    const deliveryDetailsProps: SectionProps = {
        testId: 'DeliverDetailsSection',
        className: 'awsui-util-mb-n',
        header: {
            label: 'Delivery details',
            buttons: (
                <DisplayEditButton
                    {...getEditButtonProps({
                        activity,
                        testId: 'DeliveryDetailsSectionEditButton',
                        path: 'edit-delivery-details',
                        isACIFeatureEnabled,
                    })}
                />
            ),
        },
        content: {
            type: SectionContentType.Column,
            columnsCount: 4,
            columns: [
                [
                    {
                        key: 'Course',
                        value: formatString(activity.course_name),
                    },
                    isTopicNameRequired
                        ? {
                              key: 'Topic name',
                              value: formatString(activity.topic_name),
                          }
                        : null,
                    ...formatActivityColumnData(
                        {
                            key: 'Delivery geo',
                            value: formatString(activity.delivery_geo),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                ],
                [
                    {
                        key: 'Delivery location',
                        value: (() => {
                            const location = formatStringArray([
                                activity.delivery_city,
                                activity.delivery_state,
                                activity.delivery_country,
                            ]);
                            const isILT =
                                activity.activity_modality ===
                                ActivityModality.ILT;
                            const isHybrid =
                                activity.activity_modality ===
                                ActivityModality.Hybrid;
                            const isIltOrHybrid = isILT || isHybrid;

                            return (
                                <>
                                    {!!activity.room && isIltOrHybrid && (
                                        <p>{activity.room}</p>
                                    )}
                                    {!!activity.delivery_address_1 &&
                                        isIltOrHybrid && (
                                            <p>{activity.delivery_address_1}</p>
                                        )}
                                    {!!activity.delivery_address_2 &&
                                        isIltOrHybrid && (
                                            <p>{activity.delivery_address_2}</p>
                                        )}
                                    {!!location && <p>{location}</p>}
                                    {!!activity.delivery_postal_code &&
                                        isIltOrHybrid && (
                                            <p>
                                                {activity.delivery_postal_code}
                                            </p>
                                        )}
                                </>
                            );
                        })(),
                    },
                ],
                [
                    {
                        key: 'Time zone',
                        value: formatString(activity.delivery_timezone),
                    },
                    ...formatActivityColumnData(
                        {
                            key: 'Delivery facility type',
                            value: formatString(
                                activity.delivery_facility_type,
                            ),
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                ],
                [
                    {
                        key: 'Delivery language',
                        value: formatString(activity.delivery_language),
                    },
                    ...formatActivityColumnData(
                        {
                            key: 'Facility size',
                            value: activity.facility_size ?? EMPTY_STRING,
                        },
                        activity,
                        'HYBRID_MODALITY',
                    ),
                ],
            ],
        } as ColumnContentData,
    };

    const getTableHeaderComponent = (titleStr: string, count?: number) => (
        <TableHeader
            title={titleStr}
            actions={<React.Fragment />}
            count={count ? `${count}` : ''}
        />
    );

    const emptyInstructorsTableComponent = (
        <div className="awsui-util-t-c">
            <div className="awsui-util-pt-s awsui-util-mb-xs">
                <b>No Instructors</b>
            </div>
            <p className="awsui-util-mb-s">Instructors will display here.</p>
        </div>
    );

    const emptyCostItemsTableComponent = (
        <div className="awsui-util-t-c">
            <div className="awsui-util-pt-s awsui-util-mb-xs">
                <b>No Cost items</b>
            </div>
            <p className="awsui-util-mb-s">Cost items will display here.</p>
        </div>
    );

    const generateSessionItems = () => {
        const deliverySessions = activity.delivery_sessions;
        return (
            deliverySessions?.map(
                (deliverySession) =>
                    ({
                        date: formatGrimsbyDate(
                            deliverySession.start_timestamp,
                            activity.delivery_timezone,
                        ),
                        time: formatDeliverySessionTime(
                            deliverySession,
                            activity.delivery_timezone,
                        ),
                        streamyard_url:
                            deliverySession.v_ilt_info?.streamyard_url,
                        instructors: deliverySession.instructors?.map(
                            (inst) => ({
                                pk: inst.pk,
                                name: inst.name,
                                role: inst.role,
                            }),
                        ),
                    }) as DeliverySessionItem,
            ) ?? []
        );
    };

    const generateInstructorItems = () => {
        const instructorsList = activity.instructors;
        return (
            instructorsList?.map(
                (instructor) =>
                    ({
                        pk: instructor.pk,
                        name: instructor.name,
                        role: instructor.role,
                        email: instructor.email,
                        type: instructor.type,
                        location: instructor.location,
                        doNotShuffle: formatBoolean(
                            instructor.do_not_shuffle,
                            'Do not shuffle',
                        ),
                    }) as InstructorItem,
            ) ?? []
        );
    };

    return (
        <>
            <div data-testid="ActivityDetailsPageDetailsTab">
                <Section {...activityDetailsProps} />
                <Section {...deliveryDetailsProps} />
                <Table
                    data-testid="DeliverySessionsTable"
                    columnDefinitions={deliverySessionColumnDefinition(
                        activity.program,
                        featureFlags,
                    )}
                    items={generateSessionItems()}
                    header={getTableHeaderComponent(
                        'Sessions',
                        activity.delivery_sessions.length,
                    )}
                />
                <Table
                    data-testid="DeliveryDetailsInstructorsTable"
                    columnDefinitions={instructorsColumnDefinition}
                    items={generateInstructorItems()}
                    header={
                        <Header
                            counter={
                                activity.instructors?.length
                                    ? `(${activity.instructors.length})`
                                    : ''
                            }
                            info={
                                <InfoLink
                                    onFollow={makeHelpPanelHandler(
                                        INSTRUCTOR_ASSIGNMENT_HELP_PANEL_CONTENT.title,
                                    )}
                                />
                            }
                        >
                            Instructors
                        </Header>
                    }
                    empty={emptyInstructorsTableComponent}
                />
                <section className="awsui-util-mt-l">
                    {activity.activity_audience === AudienceType.Public && (
                        <Section {...publicOperationsDetailsProps} />
                    )}
                    {activity.activity_audience !== AudienceType.Public && (
                        <Section {...otherOperationsDetailsProps} />
                    )}
                    <Section {...registrationDetailsProps} />
                    {shouldShowClassroomsDetailsTab && (
                        <Section
                            testId="ClassroomsDetailsSection"
                            header={{
                                label: 'Classrooms details',
                                buttons: (
                                    <InfoLink
                                        onFollow={makeHelpPanelHandler(
                                            CLASSROOM_PANEL_CONTENT.title,
                                        )}
                                    />
                                ),
                            }}
                            content={
                                {
                                    type: SectionContentType.Column,
                                    columnsCount: 4,
                                    columns: [
                                        [
                                            {
                                                key: 'Classroom status',
                                                value: (
                                                    <span data-testid="classroom-status">
                                                        {renderClassroomStatusMessage(
                                                            classroomStatus,
                                                            activity.course_name,
                                                        )}
                                                    </span>
                                                ),
                                            },
                                        ],
                                    ],
                                } as ColumnContentData
                            }
                        />
                    )}
                    {shouldShowClassroomsDetailsTab && (
                        <ClassroomStatusPanel activity={activity} />
                    )}
                    {isSupergraphEnabled && <SupergraphEnrichmentSection />}
                    {shouldShowClassroomsDetailsTab && hasClassroomUrls && (
                        <Section {...classroomUrlsDetailsProps} />
                    )}
                    {!!costItems?.length && (
                        <Table
                            data-testid="CostItemsTable"
                            columnDefinitions={costItemsColumnDefinition}
                            items={getCostItems()}
                            header={getTableHeaderComponent(
                                'Cost items',
                                activity.cost_items?.length,
                            )}
                            empty={emptyCostItemsTableComponent}
                        />
                    )}
                </section>
            </div>
        </>
    );
};

export default DetailsTab;
